import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  ThemeProvider,
  Typography,
} from '@material-ui/core'
import React, { PropsWithChildren } from 'react'
import { createAppTheme, rh24WebAppTheme } from 'utils/appTheme'
import { TelemetryService } from '../../services/Telemetry'
import './ErrorBoundary.scss'

type State = {
  error: Error
  errorInfo: React.ErrorInfo
  hasError: boolean
  expanded: boolean
}

export class ErrorBoundary extends React.Component<
  PropsWithChildren<unknown>,
  State
> {
  constructor(props) {
    super(props)
    this.state = {
      error: null,
      errorInfo: null,
      hasError: false,
      expanded: true,
    }
  }

  static getDerivedStateFromError = (error: Error) => {
    return { hasError: true, error: error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ error, errorInfo, expanded: false })

    TelemetryService.getInstance().logError(error)
  }

  setExpanded = (expanded: boolean) => {
    this.setState(() => ({ expanded }))
  }

  render() {
    if (this.state.error) {
      return (
        <ThemeProvider theme={createAppTheme(rh24WebAppTheme)}>
          <div className="error-boundary">
            <Typography variant="h5" color="error" className="error-header">
              Something went wrong
            </Typography>
            {window.parent === window.self ? (
              <Typography
                color="primary"
                gutterBottom
                component="a"
                style={{ display: 'block' }}
                href={`mailto:support@quotationfactory.com?subject=[webapp crash]: ${encodeURIComponent(
                  this.state.error?.toString()
                )}&body=Error found in the WebApp at \n\n${encodeURIComponent(
                  new Date().toUTCString()
                )}. \n\n${encodeURIComponent(
                  this.state.error?.toString()
                )}\n\n${encodeURIComponent(
                  this.state.errorInfo?.componentStack
                )}`}
              >
                Please send an e-mail to our support team if you need further
                assistance.
              </Typography>
            ) : null}

            <Button
              onClick={() =>
                window.location.replace(window.location.toString())
              }
              variant={'contained'}
              color="primary"
              type="submit"
            >
              reload page
            </Button>
            <Button
              onClick={() => {
                window.location.replace('/app')
              }}
              variant={'contained'}
              color="primary"
            >
              initial page
            </Button>
            <Accordion
              expanded={true}
              onChange={(e, expanded) => this.setExpanded(expanded)}
              className="error-panel"
            >
              <AccordionSummary>
                <Typography variant="caption">
                  details: {this.state.error?.toString()}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography component="p" className="error-details-title">
                  {this.state.error?.toString()}
                </Typography>
                <Typography variant="body2" className="error-details-stack">
                  {this.state.errorInfo?.componentStack}
                </Typography>
              </AccordionDetails>
            </Accordion>
          </div>
        </ThemeProvider>
      )
    }

    return this.props.children
  }
}
